// src/lib/sat/read-aloud.ts
//
// Sentence segmentation for the read-aloud / text-to-speech tool (#791). Splits
// plain text (typically a container's textContent) into sentences WITH their
// character offsets, so the speaker can read sentence-by-sentence and the UI can
// highlight the sentence currently being spoken (offsets map back to the DOM via
// rangeFromOffsets). Offsets are aligned to the SOURCE text.

export type Sentence = { text: string; start: number; end: number }

const isSpace = (c: string) => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f'
const isTerminator = (c: string) => c === '.' || c === '!' || c === '?'

/**
 * Split `text` into sentences with offsets. A sentence ends at one or more
 * terminators (. ! ?) followed by whitespace or end-of-text — so decimals
 * ("3.14") and inline dots don't split. Leading/trailing whitespace is excluded
 * from each sentence's offsets. Imperfect on abbreviations ("Dr."), which is fine
 * for read-aloud granularity.
 */
/** A superscript's place in the source text, and how it should be SAID. */
export type SpokenSub = { start: number; end: number; spoken: string }

/**
 * Pull the text to read out of a container, plus where its superscripts are.
 *
 * `text` is EXACTLY `root.textContent` — identical, character for character.
 * That is the whole point: sentence offsets, and therefore the read-along
 * highlight, keep meaning the same positions in the DOM they always did. Only
 * the string handed to the speech synthesizer changes, via `subs`.
 *
 * Without this, TTS reads a superscript's raw characters: "x⁻¹" is spoken as
 * "x minus 1" (a different value) and "2^(2x+2)" as "twenty-two x plus two",
 * because the exponent's parentheses are gone by render time.
 */
export function readAloudContent(root: HTMLElement | null | undefined): {
  text: string
  subs: SpokenSub[]
} {
  if (!root) return { text: '', subs: [] }
  const subs: SpokenSub[] = []
  let text = ''
  const walk = (node: Node): void => {
    if (node.nodeType === 3 /* Node.TEXT_NODE */) {
      text += node.nodeValue ?? ''
      return
    }
    if (node.nodeType !== 1 /* Node.ELEMENT_NODE */) return
    const el = node as HTMLElement
    // A superscript we labelled: remember where it lands and what to say.
    if (el.tagName === 'SUP' && el.getAttribute('role') === 'img') {
      const spoken = el.getAttribute('aria-label')
      const start = text.length
      for (const child of Array.from(el.childNodes)) walk(child)
      if (spoken) subs.push({ start, end: text.length, spoken })
      return
    }
    for (const child of Array.from(el.childNodes)) walk(child)
  }
  walk(root)
  return { text, subs }
}

/**
 * How one sentence should be SPOKEN: its own text with every superscript
 * falling inside it replaced by words. Offsets are source offsets, so this
 * never moves a sentence's start/end.
 */
export function spokenSentence(s: Sentence, subs: SpokenSub[]): string {
  const inside = subs
    .filter((sub) => sub.start >= s.start && sub.end <= s.end)
    .sort((a, b) => a.start - b.start)
  if (inside.length === 0) return s.text
  let out = ''
  let cursor = s.start
  for (const sub of inside) {
    out += s.text.slice(cursor - s.start, sub.start - s.start)
    out += ` ${sub.spoken} `
    cursor = sub.end
  }
  out += s.text.slice(cursor - s.start)
  return out.replace(/\s+/g, ' ').trim()
}

export function splitSentences(text: string | null | undefined): Sentence[] {
  const out: Sentence[] = []
  if (!text) return out
  const n = text.length
  let i = 0
  while (i < n) {
    while (i < n && isSpace(text[i])) i++ // skip leading whitespace
    if (i >= n) break
    const start = i
    while (i < n) {
      if (isTerminator(text[i])) {
        while (i < n && isTerminator(text[i])) i++ // consume "?!" / "..."
        if (i >= n || isSpace(text[i])) break // sentence boundary
      } else {
        i++
      }
    }
    const end = i
    const chunk = text.slice(start, end)
    if (chunk.trim() !== '') out.push({ text: chunk, start, end })
  }
  return out
}
